import { Investment } from '../../types';
import { DreamConfig, DreamProjection } from '../../types/dreamSimulator';
import { getDetailedCertificateEvolution, getCertificatePhasesSummary } from '../calculations/certificateEvolution';

/**
 * Calculates projections for a dream based on the provided configuration and investment parameters
 * @param config The dream configuration
 * @param investment The investment parameters
 * @returns Array of yearly projections
 */
export function calculateProjections(config: DreamConfig, investment: Investment): DreamProjection[] {
  const projections: DreamProjection[] = [];
  
  // Use default values if undefined
  const timeframe = config.timeframe || 15;
  const monthlyGoal = config.monthlyGoal || 50000;
  
  // Create a modified investment object with the dream configuration
  const dreamInvestment: Investment = {
    ...investment,
    initialCertificates: config.certificatesNeeded,
    years: timeframe,
    reinvestProfits: config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits
  };
  
  // Get detailed certificate evolution using the same logic as other calculators
  const certificateEvolution = getDetailedCertificateEvolution(dreamInvestment);
  
  // Process each year's data
  for (let year = 1; year <= timeframe; year++) {
    // Get the evolution data for this year
    const yearEvolution = certificateEvolution[year - 1];
    if (!yearEvolution) continue;
    
    // Get certificate phases for this year
    const phases = getCertificatePhasesSummary(certificateEvolution, year);
    
    // Calculate certificate price based on year
    let certificatePrice = yearEvolution.certificatePrice;
    
    // Calculate monthly income
    const monthlyIncome = yearEvolution.totalUtility / 12;
    
    // Calculate progress towards goal
    const progressToGoal = monthlyIncome > 0 ? Math.min(100, (monthlyIncome / monthlyGoal) * 100) : 0;
    
    // Calculate inflation-adjusted values
    const inflationFactor = Math.pow(1 + investment.inflationRate / 100, year);
    const realPatrimony = yearEvolution.citrusPatrimony / inflationFactor;
    const realMonthlyIncome = monthlyIncome / inflationFactor;
    
    projections.push({
      year,
      certificatePrice,
      totalCertificates: yearEvolution.totalCertificates,
      producingCertificates: phases.producing,
      waitingCertificates: phases.waiting + phases.reserved, // Combine waiting and reserved for simplicity
      yearlyUtility: yearEvolution.totalUtility,
      monthlyIncome,
      patrimony: yearEvolution.citrusPatrimony,
      progressToGoal,
      realPatrimony,
      realMonthlyIncome
    });
  }

  return projections;
}

/**
 * Optimizes the number of certificates needed to reach a monthly goal
 * @param config The dream configuration
 * @param investment The investment parameters
 * @returns The optimized number of certificates
 */
export function optimizeCertificatesForGoal(config: DreamConfig, investment: Investment): number {
  // Use default values if undefined
  const monthlyGoal = config.monthlyGoal || 50000;
  const timeframe = config.timeframe || 15;
  const reinvestProfits = config.reinvestProfits !== undefined ? config.reinvestProfits : investment.reinvestProfits;
  
  // Start with a low estimate and increase until we reach the goal
  let certificates = 1;
  let maxCertificates = 100; // Set a reasonable upper limit
  
  while (certificates <= maxCertificates) {
    // Create a test config with the current number of certificates
    const testConfig = {
      ...config,
      certificatesNeeded: certificates,
      reinvestProfits
    };
    
    // Calculate projections using the unified logic
    const projections = calculateProjections(testConfig, investment);
    const finalProjection = projections[projections.length - 1];
    
    // If we've reached or exceeded the goal, return this number of certificates
    if (finalProjection && finalProjection.monthlyIncome >= monthlyGoal) {
      return certificates;
    }
    
    certificates++;
  }
  
  // If we couldn't find a solution within our limit, return the max
  return maxCertificates;
}

/**
 * Calculates the total investment required for a given number of certificates
 * @param certificatesNeeded Number of certificates needed
 * @param certificatePrice Price per certificate
 * @returns The total investment amount
 */
export function calculateTotalInvestment(certificatesNeeded: number, certificatePrice: number): number {
  return certificatesNeeded * certificatePrice;
}

/**
 * Calculates the down payment amount (30% of total investment)
 * @param totalInvestment Total investment amount
 * @returns The down payment amount
 */
export function calculateDownPayment(totalInvestment: number): number {
  return totalInvestment * 0.3;
}

/**
 * Calculates the monthly payment amount (70% of total investment divided by 48 months)
 * @param totalInvestment Total investment amount
 * @returns The monthly payment amount
 */
export function calculateMonthlyPayment(totalInvestment: number): number {
  return (totalInvestment * 0.7) / 48;
}